import {
    formatMs,
} from "@mrflark/core/utils";
import {
    AdminJobsPage,
} from "@mrflark/ui";
import type {
    SettingConfig,
} from "@mrflark/ui";
import type {
    ReactElement,
} from "preact/compat";
import {
    AiPromptSettings,
} from "./AiPromptSettings.tsx";
import {
    ClearQueueButton,
} from "./ClearQueueButton.tsx";
import {
    InferencePauseButtons,
} from "./InferencePauseButtons.tsx";

const formatMinutes = (value: number): string => `${value}m`;
const formatDays = (value: number): string => `${value}d`;
const formatPercent = (value: number): string => `${(value * 100).toFixed(0)}%`;

const SETTING_CONFIGS: SettingConfig[] = [
    {
        key: "max_concurrency",
        label: "Max concurrency",
        min: 1,
        max: 4,
        step: 1,
        defaultValue: "1",
    },
    {
        key: "process_media_concurrency",
        label: "Max process media",
        min: 1,
        max: 4,
        step: 1,
        defaultValue: "1",
    },
    {
        key: "reanalyze_media_concurrency",
        label: "Max reanalyze media",
        min: 1,
        max: 4,
        step: 1,
        defaultValue: "1",
    },
    {
        key: "max_concurrent_remote_analysis",
        label: "Max remote analysis",
        min: 1,
        max: 4,
        step: 1,
        defaultValue: "1",
    },
    {
        key: "poll_interval_ms",
        label: "Poll interval",
        min: 1000,
        max: 30000,
        step: 1000,
        defaultValue: "5000",
        formatValue: formatMs,
    },
    {
        key: "cleanup_interval_ms",
        label: "Cleanup interval",
        min: 600000,
        max: 86400000,
        step: 600000,
        defaultValue: "3600000",
        formatValue: formatMs,
    },
    {
        key: "scan_interval_ms",
        label: "Scan interval",
        min: 1800000,
        max: 86400000,
        step: 1800000,
        defaultValue: "28800000",
        formatValue: formatMs,
    },
    {
        key: "stale_lock_max_age_minutes",
        label: "Stale lock age",
        min: 5,
        max: 120,
        step: 5,
        defaultValue: "30",
        formatValue: formatMinutes,
    },
    {
        key: "cleanup_retention_days",
        label: "Cleanup retention",
        min: 1,
        max: 30,
        step: 1,
        defaultValue: "7",
        formatValue: formatDays,
    },
    {
        key: "timeout_default_ms",
        label: "Default timeout",
        min: 60000,
        max: 3600000,
        step: 60000,
        defaultValue: "600000",
        formatValue: formatMs,
        group: "Timeouts",
    },
    {
        key: "timeout_process_media_ms",
        label: "Process media",
        min: 60000,
        max: 3600000,
        step: 60000,
        defaultValue: "600000",
        formatValue: formatMs,
        group: "Timeouts",
    },
    {
        key: "timeout_scan_unprocessed_ms",
        label: "Scan unprocessed",
        min: 60000,
        max: 1800000,
        step: 60000,
        defaultValue: "300000",
        formatValue: formatMs,
        group: "Timeouts",
    },
    {
        key: "timeout_rebuild_fts_index_ms",
        label: "Rebuild FTS",
        min: 60000,
        max: 3600000,
        step: 60000,
        defaultValue: "600000",
        formatValue: formatMs,
        group: "Timeouts",
    },
    {
        key: "timeout_cleanup_old_jobs_ms",
        label: "Cleanup jobs",
        min: 10000,
        max: 300000,
        step: 10000,
        defaultValue: "60000",
        formatValue: formatMs,
        group: "Timeouts",
    },
    {
        key: "hash_duplicate_threshold",
        label: "Hash duplicate",
        min: 1,
        max: 20,
        step: 1,
        defaultValue: "5",
        group: "Duplicate Detection",
    },
    {
        key: "hash_review_threshold",
        label: "Hash review",
        min: 5,
        max: 30,
        step: 1,
        defaultValue: "10",
        group: "Duplicate Detection",
    },
    {
        key: "ocr_confidence_threshold",
        label: "OCR confidence",
        min: 0.1,
        max: 0.9,
        step: 0.1,
        defaultValue: "0.5",
        formatValue: formatPercent,
        group: "OCR",
    },
    {
        key: "vision_supplement_threshold",
        label: "Vision fallback",
        min: 0.1,
        max: 0.9,
        step: 0.1,
        defaultValue: "0.6",
        formatValue: formatPercent,
        group: "OCR",
    },
];

export const ProcessingQueueTab = (): ReactElement => {
    return (
        <>
            <AdminJobsPage
                baseUrl="/api/admin/jobs"
                settingConfigs={SETTING_CONFIGS}
                headerSlot={({ refresh }) => (
                    <div className="flex items-center gap-2">
                        <InferencePauseButtons />
                        <ClearQueueButton onCleared={refresh} />
                    </div>
                )}
            />
            <div className="max-w-6xl mx-auto p-4">
                <AiPromptSettings />
            </div>
        </>
    );
};
